import { Text, VSpacer } from '@/components/DesignSystem';
import { ApiEnergyActivityRow } from '@api/interfaces/ApiEnergyDashboard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Divider, Stack, useTheme } from '@mui/material';
import { formatNumber } from '@shared/utilities';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EnergyActivityItemProps {
  row: ApiEnergyActivityRow,
}

export const EnergyActivityItem = ({ row }: EnergyActivityItemProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isRedemption = row.type === 'redeemed';
  const hasCalculation = !isRedemption && row.gallons !== null
    && row.multiplierApplied !== null && row.basePointsPerGallon !== null;
  const title = row.description
    ?? (isRedemption
      ? t(($) => $.customerDashboard.energy.activity.redemption)
      : t(($) => $.customerDashboard.energy.activity.purchase));
  const pointsLabel = row.points >= 0
    ? t(($) => $.customerDashboard.pointsActivity.pointsEarned)
    : t(($) => $.bookings.filters.pointsRedeemed);
  const DetailLine = ({ label, testID }: { label: string, testID: string }) => (
    <Text category="body-small" sx={{ color: theme.palette.text.secondary }} testID={testID}>
      {label}
    </Text>
  );

  return (
    <Stack
      data-testid={`energy-activity-${row.id}`}
      direction="row"
      onClick={() => setExpanded(!expanded)}
      p="16px"
      sx={{ border: `1px solid ${theme.palette.grey[300]}`, cursor: 'pointer' }}
    >
      <Stack flex={1} minWidth={0}>
        <Text breakWord sx={{ fontSize: '17px', fontWeight: 500 }}>{title}</Text>
        <VSpacer size="3" />
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Text category="body-large">{pointsLabel}</Text>
          <Text
            category="title-medium"
            sx={{ color: row.points >= 0 ? theme.palette.success.main : theme.palette.error.main }}
            testID="energy-activity-points"
          >
            {row.points >= 0 ? '+' : '-'}{formatNumber(Math.abs(row.points), i18n)}
          </Text>
        </Stack>
        {expanded && (
          <>
            <VSpacer size="3" />
            {row.gallons !== null && !isRedemption && (
              <DetailLine
                label={t(($) => $.customerDashboard.energy.activity.gallons, {
                  gallons: row.gallons,
                })}
                testID="energy-activity-gallons"
              />
            )}
            {row.multiplierApplied !== null && !isRedemption && (
              <DetailLine
                label={t(($) => $.customerDashboard.energy.activity.multiplier, {
                  multiplier: row.multiplierApplied,
                })}
                testID="energy-activity-multiplier"
              />
            )}
            {hasCalculation && (
              <DetailLine
                label={t(($) => $.customerDashboard.energy.activity.calculation, {
                  base: row.basePointsPerGallon,
                  gallons: row.gallons,
                  multiplier: row.multiplierApplied,
                  points: row.points,
                })}
                testID="energy-activity-calculation"
              />
            )}
          </>
        )}
      </Stack>
      <Divider flexItem orientation="vertical" sx={{ mx: '16px' }} />
      <Stack alignItems="center" justifyContent="center" sx={{ flexShrink: 0, width: '56px' }}>
        {expanded
          ? <ExpandLess sx={{ color: theme.palette.text.secondary }} />
          : <ExpandMore sx={{ color: theme.palette.text.secondary }} />}
      </Stack>
    </Stack>
  );
};
